import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"
import { useCrudContext } from "@/context/crudContext"
import ClearIcon from "@mui/icons-material/Clear"
import { Textarea } from "@mantine/core"
import Grid from "@mui/material/Grid2"
import { DevTool } from "@hookform/devtools"
import MultipleSelectChip from "@/components/Select/MultipleSelect"
import MultiSelect from "@/components/Select/MultipleSelect"
import useTags from "@/hooks/useTags"
import useAttributes from "@/hooks/useAttributes"
import { isWeightable } from "@/lib/utils"
import useBrands from "@/hooks/useBrands"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import { apiFetch } from "@/hooks/apiFetch"
import DeleteIcon from "@mui/icons-material/Delete"
import StarIcon from "@mui/icons-material/Star"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import ImageIcon from "@mui/icons-material/Image"

const AttributeSchema = z.object({
  id: z.number().positive(),
  key: z.string(),
  attribute: z.string(),
  value: z.string().optional().nullable(),
})
const TagSchema = z.object({
  id: z.number().positive(),
  name: z.string(),
})
const ProductSchema = z.object({
  name: z.string().min(3),
  brand_id: z.number().nullable(),
  sell_price: z.preprocess(
    (value) => (value === "" ? undefined : Number(value)),
    z.number().positive()
  ),
  sku: z.string().min(2),
  factory_code: z.string().nullable(),
  product_attributes: z.array(AttributeSchema),
  tags: z.array(TagSchema),
  is_weight: z.boolean().optional().nullable(),
})

const AddProductModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()

  const {
    api,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    setValue,
    control,
    Controller,
  } = useCustomForm({
    resolver: zodResolver(ProductSchema),
    defaultValues: {
      name: selectedRow?.name || "",
      sku: selectedRow?.sku || "",
      factory_code: selectedRow?.factory_code || "",
      sell_price: selectedRow?.sell_price || "",
      product_attributes: selectedRow?.product_attributes || [],
      tags: selectedRow?.tags || [],
      brand_id: selectedRow?.brand_id || null,
      is_weight: selectedRow?.is_weight || false,
    },
  })
  const { attributes, isLoading: attributeLoading } = useAttributes()
  const { tags, isLoading: tagLoading } = useTags()
  const { brands, isLoading: brandLoading } = useBrands()
  const [images, setImages] = useState([])
  const [pendingImages, setPendingImages] = useState([]) // For new product creation
  const [uploading, setUploading] = useState(false)
  const defaultOnClose = () => {
    setOpenDialog(false)
    setImages([])
    setPendingImages([])
  }

  useEffect(() => {
    if (selectedRow?.images) {
      setImages(selectedRow.images)
    } else {
      setImages([])
    }
    setPendingImages([]) // Clear pending images when switching products
  }, [selectedRow])

  const onSubmit = async (data) => {
    const method = selectedRow ? "PUT" : "POST" // Use PUT for updating, POST for adding
    const url = selectedRow ? `product/${selectedRow.id}` : "product"

    const response = await api(url, {
      method,
      body: JSON.stringify(data),
    })

    if (response) {
      const productId = response?.data?.id || selectedRow?.id

      // If there are pending images and product was just created, upload them
      if (!selectedRow && pendingImages.length > 0 && productId) {
        try {
          await handleUploadPendingImages(productId)
        } catch (error) {
          toast.error("Product created but failed to upload images")
        }
      }

      queryClient.invalidateQueries({ queryKey: ["product"] })
      queryClient.invalidateQueries({ queryKey: ["product-detail", productId] })
      toast.success(
        `Product  successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
      setPendingImages([])
    }
  }

  const handleUploadPendingImages = async (productId) => {
    if (pendingImages.length === 0) return

    const formData = new FormData()
    pendingImages.forEach((image) => {
      formData.append("images[]", image.file)
    })

    try {
      const response = await apiFetch(`product/${productId}/images`, {
        method: "POST",
        body: formData,
      })

      if (response?.data) {
        toast.success("Images uploaded successfully")
      }
    } catch (error) {
      throw error
    }
  }

  const handleReset = () => {
    reset({
      name: selectedRow?.name || "",
      sku: selectedRow?.sku || "",
      factory_code: selectedRow?.factory_code || "",
      sell_price: selectedRow?.sell_price || "",
      product_attributes: selectedRow?.product_attributes || [],
      tags: selectedRow?.tags || [],
      brand_id: selectedRow?.brand_id || null,
      is_weight: selectedRow?.is_weight || false,
    })
    if (selectedRow?.images) {
      setImages(selectedRow.images)
    } else {
      setImages([])
    }
    setPendingImages([])
  }

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    // If editing existing product, upload immediately
    if (selectedRow?.id) {
      setUploading(true)
      try {
        const formData = new FormData()
        files.forEach((file) => {
          formData.append("images[]", file)
        })

        const response = await apiFetch(`product/${selectedRow.id}/images`, {
          method: "POST",
          body: formData,
        })

        if (response?.data) {
          setImages([...images, ...response.data])
          queryClient.invalidateQueries({
            queryKey: ["product-detail", selectedRow.id],
          })
          toast.success("Images uploaded successfully")
        }
      } catch (error) {
        toast.error(error?.message || "Failed to upload images")
      } finally {
        setUploading(false)
        e.target.value = ""
      }
    } else {
      // If creating new product, store files temporarily
      const newFiles = files.map((file) => ({
        id: Date.now() + Math.random(),
        file,
        url: URL.createObjectURL(file),
        original_name: file.name,
        is_thumbnail: pendingImages.length === 0, // First image is thumbnail
      }))
      setPendingImages([...pendingImages, ...newFiles])
      e.target.value = ""
    }
  }

  const handleDeleteImage = async (imageId) => {
    // If it's a pending image (new product), just remove from state
    if (pendingImages.some((img) => img.id === imageId)) {
      const updated = pendingImages.filter((img) => img.id !== imageId)
      // If deleted was thumbnail, set first remaining as thumbnail
      if (
        pendingImages.find((img) => img.id === imageId)?.is_thumbnail &&
        updated.length > 0
      ) {
        updated[0].is_thumbnail = true
      }
      setPendingImages(updated)
      return
    }

    // If it's an existing image, delete from server
    try {
      await apiFetch(`image/${imageId}`, {
        method: "DELETE",
      })
      setImages(images.filter((img) => img.id !== imageId))
      queryClient.invalidateQueries({
        queryKey: ["product-detail", selectedRow?.id],
      })
      toast.success("Image deleted successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to delete image")
    }
  }

  const handleSetThumbnail = async (imageId) => {
    // If it's a pending image (new product), just update state
    if (pendingImages.some((img) => img.id === imageId)) {
      setPendingImages(
        pendingImages.map((img) => ({
          ...img,
          is_thumbnail: img.id === imageId,
        }))
      )
      return
    }

    // If it's an existing image, update on server
    try {
      await apiFetch(`image/${imageId}/set-thumbnail`, {
        method: "POST",
      })
      setImages(
        images.map((img) => ({
          ...img,
          is_thumbnail: img.id === imageId,
        }))
      )
      queryClient.invalidateQueries({
        queryKey: ["product", selectedRow?.id],
      })
      toast.success("Thumbnail updated successfully")
    } catch (error) {
      toast.error(error?.message || "Failed to set thumbnail")
    }
  }

  return (
    <Modal
      open={open ? open : openDialog}
      onClose={defaultOnClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          width: 1000,
          margin: "100px auto",
          bgcolor: "white",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 1,
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 1,
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update Product " : "Add New Product "}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                fullWidth
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register("name")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                fullWidth
                label="SKU"
                error={!!errors.sku}
                helperText={errors.sku?.message}
                {...register("sku")}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                fullWidth
                label="Factory Code"
                error={!!errors.factory_code}
                helperText={errors.factory_code?.message}
                {...register("factory_code")}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <SingleSelect
                name="brand_id"
                controller={Controller}
                control={control}
                loading={brandLoading}
                label="Brands"
                selectedValue={selectedRow?.brand_id}
                options={brands ?? []}
                error={!!errors.brand_id}
                helperText={errors.brand_id}
                optionLabel={(option) => `${option.name}`}
                onInputChange={(searchText, value) => {
                  console.log("input changed", searchText, value)
                  // if (value) setCustomerSearchQuery(searchText)
                }}
                optionValue="id"
                renderOptionContent={(option) => (
                  <LetterAvatar name={option?.name} key={option.id} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                error={!!errors.sell_price}
                helperText={errors.sell_price?.message}
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                {...register("sell_price")}
              />
            </Grid>
            <Grid size={{ sm: 12, md: 12, lg: 12 }}>
              <MultiSelect
                name="tags"
                label="Tags"
                options={tags}
                control={control}
                loading={tagLoading}
                defaultValues={selectedRow?.tags}
                controller={Controller}
                optionLabel={["name"]}
                error={!!errors.tags}
                helperText={errors.tags?.message}
              />
            </Grid>
            <Grid size={{ sm: 12, md: 12, lg: 10 }}>
              <MultiSelect
                name="product_attributes"
                label="Attributes"
                loading={attributeLoading}
                options={attributes}
                defaultValues={selectedRow?.product_attributes}
                control={control}
                controller={Controller}
                optionLabel={["key", "attribute"]}
                error={!!errors.product_attributes}
                helperText={errors.product_attributes?.message}
              />
            </Grid>
            <Grid size={{ sm: 12, md: 12, lg: 2 }}>
              {isWeightable() && (
                <Controller
                  name="is_weight"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Weightable"
                    />
                  )}
                />
              )}
            </Grid>

            {/* Image Upload Section - Show for both new and existing products */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Product Images
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="image-upload"
                  multiple
                  type="file"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<ImageIcon />}
                    disabled={uploading}
                    sx={{ mb: 2 }}
                  >
                    {uploading ? "Uploading..." : "Upload Images"}
                  </Button>
                </label>
              </Box>

              {/* Show existing images (for editing) */}
              {images.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  {images.map((image) => (
                    <Box
                      key={image.id}
                      sx={{
                        position: "relative",
                        border: image.is_thumbnail
                          ? "3px solid #1976d2"
                          : "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        aspectRatio: "1",
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.original_name || "Product image"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSetThumbnail(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title={
                            image.is_thumbnail
                              ? "Thumbnail"
                              : "Set as thumbnail"
                          }
                        >
                          {image.is_thumbnail ? (
                            <StarIcon color="primary" />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteImage(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title="Delete image"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                      {image.is_thumbnail && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(25, 118, 210, 0.9)",
                            color: "white",
                            textAlign: "center",
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          Thumbnail
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Show pending images (for new product creation) */}
              {pendingImages.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                  }}
                >
                  {pendingImages.map((image) => (
                    <Box
                      key={image.id}
                      sx={{
                        position: "relative",
                        border: image.is_thumbnail
                          ? "3px solid #1976d2"
                          : "1px solid #e0e0e0",
                        borderRadius: 1,
                        overflow: "hidden",
                        aspectRatio: "1",
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.original_name || "Product image"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSetThumbnail(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title={
                            image.is_thumbnail
                              ? "Thumbnail"
                              : "Set as thumbnail"
                          }
                        >
                          {image.is_thumbnail ? (
                            <StarIcon color="primary" />
                          ) : (
                            <StarBorderIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteImage(image.id)}
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
                          }}
                          title="Remove image"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                      {image.is_thumbnail && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: "rgba(25, 118, 210, 0.9)",
                            color: "white",
                            textAlign: "center",
                            py: 0.5,
                            fontSize: "0.75rem",
                          }}
                        >
                          Thumbnail
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* buttons */}
          <Grid size={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "10px",
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
          <DevTool control={control} />
        </Box>
      </Box>
    </Modal>
  )
}

export default AddProductModal
